import { Badge, Box, Heading, Link } from "@chakra-ui/layout";
import {
  useColorModeValue,
  Stack,
  VStack,
  HStack,
  Button,
  Radio,
  FormControl,
  FormLabel,
  Input,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import {
  InputControl,
  RadioGroupControl,
  SelectControl,
  SubmitButton,
} from "formik-chakra-ui";
import { FC, useMemo, useState } from "react";
import TagInput from "components/TagInput";
import { useMutation, useQueryClient } from "react-query";
import { updateTFQuestion } from "API/questionApi";
import { Link as RoLink } from "react-router-dom";
import * as Yup from "yup";
import { STATIC_PREFIX } from "utils/constants";


/* Types */
interface ITFQuestion {
  _id: string;
  question: string;
  positiveMarks?: number;
  negativeMarks?: number;
  answerJustification?: string;
  tags: string[];
  level: "easy" | "normal" | "difficult";
  tFAnswer: boolean;
  questionImg?: string;
  justificationImg?: string;
  subjectName: string;
  isPremium: boolean;
  type: string;
}
interface UpdateQuestionTFProps {
  question: ITFQuestion;
  role: string | null | undefined;
}


/* Image URL strategy */
const getBackendBaseUrl = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/:3000$/, ":9000").replace(/\/+$/, "");
  }
  return "";
};

const buildStoredImageUrl = (val?: string): string => {
  if (!val || typeof val !== "string") return "";
  if (/^https?:\/\//i.test(val)) return val;
  const base = getBackendBaseUrl();
  return `${base}${STATIC_PREFIX}/${encodeURIComponent(val)}`;
};


/* File constraints: JPG/PNG up to 2 MB */
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png"];
const fileErrorText = "Only JPG or PNG up to 2 MB allowed.";


const isValidUpload = (file: File | null) => {
  if (!file) return true;
  if (!ALLOWED_TYPES.includes(file.type)) return false;
  if (file.size > MAX_FILE_BYTES) return false;
  return true;
};


const UpdateQuestionTF: FC<UpdateQuestionTFProps> = ({ question, role }) => {
  const [tags, setTags] = useState<string[]>([...(question?.tags || [])]);


  // Local state for newly selected files
  const [questionImageFile, setQuestionImageFile] = useState<File | null>(null);
  const [justificationImageFile, setJustificationImageFile] = useState<File | null>(null);
  const [questionImgError, setQuestionImgError] = useState<string | null>(null);
  const [justificationImgError, setJustificationImgError] = useState<string | null>(null);


  const toast = useToast();
  const cache = useQueryClient();


  const { isLoading, mutateAsync } = useMutation(updateTFQuestion, {
    onSuccess: () => {
      cache.invalidateQueries(["question", question._id]);
      toast({ title: "Question Updated Successfully", status: "success" });
    },
    onError: (err: any) => {
      let msg = err?.message || "Something went wrong.";
      if (err?.code === 11000 || (typeof err?.message === "string" && /duplicate/i.test(err.message))) {
        msg = "Duplicate question already exists. Please use a unique question.";
      }
      toast({
        title: "Error",
        description: msg,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });


  const initialValues = useMemo(
    () => ({
      question: question.question || "",
      positiveMarks: Number.isFinite(question.positiveMarks) ? question.positiveMarks : 2,
      negativeMarks: Number.isFinite(question.negativeMarks) ? question.negativeMarks : -1,
      answerJustification: question.answerJustification || "",
      tags,
      level: (question.level || "normal") as any,
      tFAnswer: question.tFAnswer ? "true" : "false",
      questionImg: question?.questionImg || "",
      justificationImg: question?.justificationImg || "",
    }),
    [question, tags]
  );


  // Validation similar to MCQS/MCQM, adapted for TF
  const validationSchema = Yup.object({
    question: Yup.string().required("Question is required"),
    positiveMarks: Yup.number().required("Required"),
    negativeMarks: Yup.number().required("Required"),
    tFAnswer: Yup.string().oneOf(["true", "false"]).required("Please select True or False"),
  });


  return (
    <>
      <Heading size="md" mb="4">
        <Link as={RoLink} to={`/${role}/dashboard/questions`} color="teal.500">
          Questions
        </Link>{" "}
        / Edit True False Question
      </Heading>


      <HStack justify="space-between">
        <Badge>{question.subjectName}</Badge>
        {question.isPremium && <Badge colorScheme="yellow">Premium</Badge>}
      </HStack>


      <Box
        my="20px"
        border="1px"
        borderColor={useColorModeValue("gray.100", "gray.700")}
        borderRadius="md"
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            // Block submission if files invalid
            if (!isValidUpload(questionImageFile)) {
              setQuestionImgError(fileErrorText);
              return;
            }
            if (!isValidUpload(justificationImageFile)) {
              setJustificationImgError(fileErrorText);
              return;
            }
            setQuestionImgError(null);
            setJustificationImgError(null);


            const form = new FormData();
            form.append("_id", String(question._id));
            form.append("type", String(question.type));
            form.append("question", values.question);
            form.append("positiveMarks", String(values.positiveMarks));
            form.append("negativeMarks", String(values.negativeMarks));
            form.append("answerJustification", values.answerJustification);
            form.append("level", String(values.level || "normal"));
            (tags || []).forEach((t) => form.append("tags[]", t));
            form.append("tFAnswer", values.tFAnswer);


            if (questionImageFile) form.append("questionImageFile", questionImageFile);
            if (justificationImageFile) form.append("justificationImageFile", justificationImageFile);


            try {
              await mutateAsync(form as any);
            } catch {
              // handled by onError
            }
          }}
        >
          <Form>
            <Stack direction={["column", "row"]} spacing="20px">
              <VStack
                w={{ base: "100%", lg: "30%" }}
                spacing="15px"
                padding="20px"
                bg={useColorModeValue("gray.50", "gray.700")}
              >
                <SelectControl
                  isRequired
                  name="level"
                  selectProps={{ rounded: "md" }}
                >
                  <option value="easy">Easy</option>
                  <option value="normal">Normal</option>
                  <option value="difficult">Difficult</option>
                </SelectControl>
                <TagInput tags={tags} setTags={setTags} />
              </VStack>


              <Box w={{ base: "100%", lg: "70%" }} padding="20px">
                <VStack align="stretch" spacing={4}>
                  <InputControl label="Question" name="question" />


                  <RadioGroupControl
                    name="tFAnswer"
                    label="Correct Answer"
                  >
                    <Radio value="true">True</Radio>
                    <Radio value="false">False</Radio>
                  </RadioGroupControl>


                  <InputControl
                    label="Answer Justification"
                    name="answerJustification"
                  />


                  {/* Marks block - removed Question Marks field */}
                  <VStack align="stretch" spacing={3}>
                    <InputControl
                      label="Positive Marks"
                      name="positiveMarks"
                      inputProps={{ type: "number" }}
                    />
                    <InputControl
                      label="Negative Marks"
                      name="negativeMarks"
                      inputProps={{ type: "number" }}
                    />
                  </VStack>


                  {/* Question Image */}
                  <VStack align="stretch" spacing={2} mt={2}>
                    <FormControl>
                      <FormLabel>Question Image</FormLabel>
                      <Input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => {
                          const f = e.currentTarget.files?.[0] || null;
                          if (f && !isValidUpload(f)) {
                            setQuestionImgError(fileErrorText);
                            setQuestionImageFile(null);
                          } else {
                            setQuestionImgError(null);
                            setQuestionImageFile(f);
                          }
                        }}
                      />
                      {questionImgError && (
                        <Text color="red.400" fontSize="sm">{questionImgError}</Text>
                      )}
                    </FormControl>
                    {!questionImageFile && initialValues.questionImg && (
                      <Box>
                        <Image
                          src={buildStoredImageUrl(initialValues.questionImg)}
                          alt="Question"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                    {questionImageFile && (
                      <Box>
                        <Image
                          src={URL.createObjectURL(questionImageFile)}
                          alt="Question (new)"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </VStack>


                  {/* Justification Image */}
                  <VStack align="stretch" spacing={2} mt={2}>
                    <FormControl>
                      <FormLabel>Justification Image</FormLabel>
                      <Input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={(e) => {
                          const f = e.currentTarget.files?.[0] || null;
                          if (f && !isValidUpload(f)) {
                            setJustificationImgError(fileErrorText);
                            setJustificationImageFile(null);
                          } else {
                            setJustificationImgError(null);
                            setJustificationImageFile(f);
                          }
                        }}
                      />
                      {justificationImgError && (
                        <Text color="red.400" fontSize="sm">{justificationImgError}</Text>
                      )}
                    </FormControl>
                    {!justificationImageFile && initialValues.justificationImg && (
                      <Box>
                        <Image
                          src={buildStoredImageUrl(initialValues.justificationImg)}
                          alt="Justification"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                    {justificationImageFile && (
                      <Box>
                        <Image
                          src={URL.createObjectURL(justificationImageFile)}
                          alt="Justification (new)"
                          maxH="160px"
                          objectFit="contain"
                          borderWidth="1px"
                          borderRadius="md"
                        />
                      </Box>
                    )}
                  </VStack>


                  <HStack direction="row" spacing={4} align="center" mt="20px">
                    <SubmitButton
                      type="submit"
                      colorScheme="teal"
                      variant="solid"
                      px="30px"
                      isLoading={isLoading}
                    >
                      Save
                    </SubmitButton>
                    <Button
                      as={RoLink}
                      to={`/${role}/dashboard/questions`}
                      type="reset"
                      colorScheme="teal"
                      variant="ghost"
                    >
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            </Stack>
          </Form>
        </Formik>
      </Box>
    </>
  );
};
export default UpdateQuestionTF;
